import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { 
  Settings, CreditCard, User, MapPin, Loader2, ExternalLink,
  CheckCircle2, Search, Filter, Bell, BellOff, Key, TrendingUp,
  FileText, Shield, Receipt, Trash2, AlertTriangle, BarChart3
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { user, token, refreshUser, logout } = useAuth();
  const { isSupported, isEnabled, permission, enableWeatherNotifications, disableWeatherNotifications } = useNotifications();
  const [saving, setSaving] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [enablingNotifications, setEnablingNotifications] = useState(false);
  const [formData, setFormData] = useState({
    farm_name: user?.farm_name || '',
    farm_type: user?.farm_type || 'mixed',
    location_lat: user?.location_lat || '',
    location_lon: user?.location_lon || '',
    usda_api_key: user?.usda_api_key || ''
  });

  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Check if user is admin on mount
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAdmin(res.data.is_admin);
      } catch (error) {
        setIsAdmin(false);
      }
    };
    if (token) checkAdmin();
  }, [token]);

  // Account deletion state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Chore management state
  const [choresDialogOpen, setChoresDialogOpen] = useState(false);
  const [availableChores, setAvailableChores] = useState([]);
  const [selectedChoreIds, setSelectedChoreIds] = useState([]);
  const [choresLoading, setChoresLoading] = useState(false);
  const [choresSaving, setChoresSaving] = useState(false);
  const [choreSearch, setChoreSearch] = useState('');
  const [choreCategory, setChoreCategory] = useState('all');
  const [categories, setCategories] = useState([]);

  const fetchChoresData = async () => {
    setChoresLoading(true);
    try {
      const [availableRes, selectedRes] = await Promise.all([
        axios.get(`${API_URL}/api/chores/available`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/chores/selected`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setAvailableChores(availableRes.data.chores || []);
      setCategories(availableRes.data.categories || []);
      setSelectedChoreIds(selectedRes.data.chore_ids || []);
    } catch (error) {
      console.error('Failed to fetch chores:', error);
    } finally {
      setChoresLoading(false);
    }
  };

  // Fetch selected chore count on initial load
  useEffect(() => {
    const fetchSelectedCount = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/chores/selected`, { headers: { Authorization: `Bearer ${token}` } });
        setSelectedChoreIds(res.data.chore_ids || []);
      } catch (error) {
        console.error('Failed to fetch selected chores:', error);
      }
    };
    fetchSelectedCount();
  }, [token]);

  useEffect(() => {
    if (choresDialogOpen) {
      fetchChoresData();
    }
  }, [choresDialogOpen]);

  // ---- Grant & Federal Identifiers profile (Feb 2026) ----
  // Owns the /api/farm-profile endpoint. Rendered as a separate card on
  // Settings so users have ONE canonical place to enter identifiers used
  // for USDA, FSA, NRCS, VAPG, and SAM.gov submissions.
  const [profileForm, setProfileForm] = useState({
    legal_entity_name: '',
    dba_llc_names: '',
    usda_farm_number: '',
    fsa_tract_number: '',
    uei: '',
    duns: '',
    ein: '',
    address: '',
    phone: '',
    contact_email: '',
  });
  const [profileEinMasked, setProfileEinMasked] = useState('');
  const [profileEinSet, setProfileEinSet] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/farm-profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const p = res.data || {};
        setProfileForm({
          legal_entity_name: p.legal_entity_name || '',
          dba_llc_names: p.dba_llc_names || '',
          usda_farm_number: p.usda_farm_number || '',
          fsa_tract_number: p.fsa_tract_number || '',
          uei: p.uei || '',
          duns: p.duns || '',
          ein: '', // never returned; leave blank so user can enter fresh
          address: p.address || '',
          phone: p.phone || '',
          contact_email: p.contact_email || '',
        });
        setProfileEinMasked(p.ein_masked || '');
        setProfileEinSet(!!p.ein_set);
      } catch {
        /* leave form blank on error */
      } finally {
        setProfileLoaded(true);
      }
    };
    if (token) loadProfile();
  }, [token]);

  const handleSaveGrantProfile = async () => {
    // Basic client-side sanity: UEI is 12 alphanumeric on SAM.gov.
    if (profileForm.uei && !/^[A-Za-z0-9]{12}$/.test(profileForm.uei.trim())) {
      toast.error('UEI must be exactly 12 letters/digits (SAM.gov format).');
      return;
    }
    if (profileForm.duns && !/^\d{9}$/.test(profileForm.duns.trim())) {
      toast.error('DUNS must be a 9-digit number.');
      return;
    }
    setProfileSaving(true);
    try {
      const payload = { ...profileForm };
      // Only send EIN if user typed a new one (empty means "no change").
      if (!payload.ein) delete payload.ein;
      const res = await axios.put(`${API_URL}/api/farm-profile`, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const p = res.data || {};
      setProfileEinMasked(p.ein_masked || '');
      setProfileEinSet(!!p.ein_set);
      setProfileForm((prev) => ({ ...prev, ein: '' }));
      toast.success('Grant profile saved');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Failed to save profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/auth/profile`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            farm_name: formData.farm_name || undefined,
            farm_type: formData.farm_type || undefined,
            location_lat: formData.location_lat ? parseFloat(formData.location_lat) : undefined,
            location_lon: formData.location_lon ? parseFloat(formData.location_lon) : undefined,
            usda_api_key: formData.usda_api_key || undefined
          }
        }
      );
      await refreshUser();
      toast.success('Profile updated');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/auth/account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Account deleted successfully');
      setDeleteDialogOpen(false);
      // Log out and redirect to landing page
      logout();
    } catch (error) {
      console.error('Failed to delete account:', error);
      toast.error('Failed to delete account. Please try again or contact support.');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveChores = async () => {
    setChoresSaving(true);
    try {
      await axios.post(`${API_URL}/api/chores/selected`, {
        chore_ids: selectedChoreIds
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Saved ${selectedChoreIds.length} daily chores`);
      setChoresDialogOpen(false);
    } catch (error) {
      console.error('Failed to save chores:', error);
      toast.error('Failed to save chores');
    } finally {
      setChoresSaving(false);
    }
  };

  const toggleChore = (choreId) => {
    setSelectedChoreIds(prev => 
      prev.includes(choreId) 
        ? prev.filter(id => id !== choreId)
        : [...prev, choreId]
    );
  };

  const filteredChores = availableChores.filter(chore => {
    const matchesSearch = !choreSearch || 
      chore.name.toLowerCase().includes(choreSearch.toLowerCase());
    const matchesCategory = choreCategory === 'all' || 
      chore.category === choreCategory;
    return matchesSearch && matchesCategory;
  });

  // Plan selection state
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [showPlanDialog, setShowPlanDialog] = useState(false);

  const plans = [
    { id: 'monthly', name: 'Monthly', price: '$9.99', interval: '/month', description: 'Billed monthly', badge: null },
    { id: 'annual', name: 'Annual', price: '$99', interval: '/year', description: 'Save $20/year', badge: 'Best Value' },
    { id: 'founding_annual', name: 'Founding Member', price: '$79', interval: '/year', description: 'Limited time offer', badge: 'Limited' }
  ];

  const handleSubscribe = async (planId = selectedPlan) => {
    setSubscribing(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/billing/create-checkout`,
        { origin_url: window.location.origin, plan_id: planId },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout');
      setSubscribing(false);
    }
  };

  const handleNotificationToggle = async () => {
    if (isEnabled) {
      disableWeatherNotifications();
      toast.success('Weather notifications disabled');
    } else {
      setEnablingNotifications(true);
      try {
        const success = await enableWeatherNotifications(token);
        if (success) {
          toast.success('Weather notifications enabled! You will be alerted for severe weather.');
        } else {
          toast.error('Could not enable notifications. Please allow notifications in your browser settings.');
        }
      } catch (error) {
        console.error('Notification error:', error);
        toast.error('Failed to enable notifications');
      } finally {
        setEnablingNotifications(false);
      }
    }
  };

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData({
          ...formData,
          location_lat: position.coords.latitude.toFixed(4),
          location_lon: position.coords.longitude.toFixed(4)
        });
        toast.success('Location detected');
      },
      () => toast.error('Could not get location')
    );
  };

  const subscriptionStatus = user?.subscription_status || 'trial';
  const trialEnds = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
  const trialDaysLeft = trialEnds ? Math.max(0, Math.ceil((trialEnds - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  const getCategoryIcon = (catId) => {
    const icons = {
      animals: '🐄', poultry: '🐔', dairy: '🥛', garden: '🌱',
      orchard: '🍎', equipment: '🔧', planning: '📋', bees: '🐝',
      aquaculture: '🐟', processing: '🍳'
    };
    return icons[catId] || '📌';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
          Settings
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage your account and farm preferences
        </p>
      </div>

      {/* Admin Dashboard Card - Only visible to admins */}
      {isAdmin && (
        <Card className="card-farm border-purple-500/30 bg-purple-50/30" data-testid="admin-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              Admin Dashboard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              View user engagement statistics and app usage metrics.
            </p>
            <Link to="/app/admin">
              <Button className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700">
                <BarChart3 className="w-4 h-4 mr-2" />
                Open Admin Dashboard
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Daily Chores Card - NEW */}
      <Card className="card-farm border-green-500/30" data-testid="chores-settings-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            Daily Chores Checklist
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Select the daily chores you want to track. These will appear at the top of your Daily Advisor page.
          </p>
          
          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
            <div>
              <span className="font-medium text-foreground">
                {selectedChoreIds.length > 0 ? `${selectedChoreIds.length} chores selected` : 'No chores selected'}
              </span>
              <p className="text-xs text-muted-foreground mt-0.5">
                Resets daily at midnight
              </p>
            </div>
            <Dialog open={choresDialogOpen} onOpenChange={setChoresDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="manage-chores-btn">
                  {selectedChoreIds.length > 0 ? 'Edit Chores' : 'Set Up Chores'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="font-heading">Manage Daily Chores</DialogTitle>
                </DialogHeader>
                
                {choresLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <>
                    {/* Search and Filter */}
                    <div className="flex flex-col sm:flex-row gap-3 py-3">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search chores..."
                          value={choreSearch}
                          onChange={(e) => setChoreSearch(e.target.value)}
                          className="pl-10"
                          data-testid="chore-search-input"
                        />
                      </div>
                      <Select value={choreCategory} onValueChange={setChoreCategory}>
                        <SelectTrigger className="w-full sm:w-48" data-testid="chore-category-filter">
                          <Filter className="w-4 h-4 mr-2" />
                          <SelectValue placeholder="All Categories" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Categories</SelectItem>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {getCategoryIcon(cat.id)} {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="text-sm text-muted-foreground mb-2">
                      {selectedChoreIds.length} selected • {filteredChores.length} shown
                    </div>

                    {/* Chore List */}
                    <div className="flex-1 overflow-y-auto space-y-1 pr-2 max-h-[400px]">
                      {filteredChores.map((chore) => {
                        const isSelected = selectedChoreIds.includes(chore.id);
                        const category = categories.find(c => c.id === chore.category);
                        
                        return (
                          <label
                            key={chore.id}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                              isSelected 
                                ? 'bg-green-50 border border-green-200' 
                                : 'bg-muted/30 hover:bg-muted/50 border border-transparent'
                            }`}
                            data-testid={`chore-option-${chore.id}`}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleChore(chore.id)}
                              className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                            />
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-foreground">
                                {chore.name}
                              </span>
                              <div className="flex items-center gap-2 mt-0.5">
                                <Badge variant="outline" className="text-xs">
                                  {getCategoryIcon(chore.category)} {category?.name || chore.category}
                                </Badge>
                                {chore.season !== 'all' && (
                                  <span className="text-xs text-muted-foreground">
                                    {chore.season}
                                  </span>
                                )}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button variant="outline" onClick={() => setChoresDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleSaveChores}
                        disabled={choresSaving}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="save-chores-btn"
                      >
                        {choresSaving ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                        )}
                        Save {selectedChoreIds.length} Chores
                      </Button>
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Weather Notifications Card */}
      <Card className="card-farm border-blue-500/30" data-testid="notifications-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            {isEnabled ? (
              <Bell className="w-5 h-5 text-blue-600" />
            ) : (
              <BellOff className="w-5 h-5 text-muted-foreground" />
            )}
            Weather Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Get browser notifications when severe weather is detected for your farm location.
          </p>
          
          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
            <div>
              <span className="font-medium text-foreground">
                {isEnabled ? 'Notifications Enabled' : 'Notifications Disabled'}
              </span>
              <p className="text-xs text-muted-foreground mt-0.5">
                {!isSupported ? 'Not supported in this browser' :
                 permission === 'denied' ? 'Blocked by browser - check your settings' :
                 isEnabled ? 'You will receive alerts for severe weather' :
                 'Enable to receive weather alerts'}
              </p>
            </div>
            <Button
              variant={isEnabled ? "default" : "outline"}
              onClick={handleNotificationToggle}
              disabled={!isSupported || permission === 'denied' || enablingNotifications}
              className={isEnabled ? 'bg-blue-600 hover:bg-blue-700' : ''}
              data-testid="toggle-notifications-btn"
            >
              {enablingNotifications ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isEnabled ? (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  On
                </>
              ) : (
                <>
                  <BellOff className="w-4 h-4 mr-2" />
                  Off
                </>
              )}
            </Button>
          </div>
          
          {permission === 'denied' && (
            <p className="text-xs text-destructive">
              Notifications are blocked. To enable them, click the lock icon in your browser&apos;s address bar and allow notifications.
            </p>
          )}
        </CardContent>
      </Card>

      {/* USDA Market API Card */}
      <Card className="card-farm border-emerald-500/30" data-testid="usda-api-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            USDA Market Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Connect to USDA Market News for live commodity prices. Get real-time pricing for cattle, sheep, goats, poultry, dairy, and produce.
          </p>
          
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="usda_api_key" className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                API Key
              </Label>
              <Input
                id="usda_api_key"
                type="password"
                value={formData.usda_api_key}
                onChange={(e) => setFormData({ ...formData, usda_api_key: e.target.value })}
                placeholder="Enter your USDA Market News API key"
                className="input-farm"
                data-testid="usda-api-key-input"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div>
                <span className="font-medium text-foreground text-sm">
                  {formData.usda_api_key ? 'API Key Configured' : 'No API Key'}
                </span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {formData.usda_api_key 
                    ? 'Live market data enabled on Market Insights page' 
                    : 'Add key to see live USDA prices'}
                </p>
              </div>
              {formData.usda_api_key && (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              )}
            </div>
            
            <div className="text-xs text-muted-foreground space-y-1">
              <p className="font-medium">How to get your API key:</p>
              <ol className="list-decimal list-inside space-y-1 ml-1">
                <li>Visit <a href="https://mymarketnews.ams.usda.gov" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">mymarketnews.ams.usda.gov</a></li>
                <li>Create a free account</li>
                <li>Go to your profile to find your API key</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Card */}
      <Card className="card-farm" data-testid="subscription-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            Subscription
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-foreground">Current Plan</span>
                <Badge variant={
                  subscriptionStatus === 'active' ? 'default' :
                  subscriptionStatus === 'comped' ? 'default' :
                  subscriptionStatus === 'past_due' ? 'destructive' : 'secondary'
                } data-testid="subscription-status-badge">
                  {subscriptionStatus === 'active' ? 'Active' :
                   subscriptionStatus === 'comped' ? 'Comped' :
                   subscriptionStatus === 'past_due' ? 'Past Due' :
                   subscriptionStatus === 'canceled' ? 'Canceled' : 'Trial'}
                </Badge>
              </div>
              {subscriptionStatus === 'trial' && trialDaysLeft > 0 && (
                <p className="text-sm text-muted-foreground">
                  {trialDaysLeft} days left in trial
                </p>
              )}
              {subscriptionStatus === 'active' && (
                <p className="text-sm text-muted-foreground">
                  Subscribed
                </p>
              )}
              {subscriptionStatus === 'comped' && user?.current_period_end && (
                <p className="text-sm text-muted-foreground" data-testid="comped-expiry">
                  Comped — expires {new Date(user.current_period_end).toLocaleDateString()}
                </p>
              )}
              {subscriptionStatus === 'past_due' && (
                <p className="text-sm text-destructive">
                  Payment failed — update your card to keep access
                </p>
              )}
            </div>
            {subscriptionStatus !== 'active' && subscriptionStatus !== 'comped' && (
              <Button 
                onClick={() => setShowPlanDialog(true)}
                className="btn-primary"
                disabled={subscribing}
                data-testid="subscribe-btn"
              >
                {subscribing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Choose Plan
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
          
          {/* Plan Selection Dialog */}
          <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Choose Your Plan</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-4">
                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedPlan === plan.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    {plan.badge && (
                      <Badge 
                        className={`absolute -top-2 right-3 ${
                          plan.badge === 'Best Value' ? 'bg-green-600' : 'bg-amber-600'
                        }`}
                      >
                        {plan.badge}
                      </Badge>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-foreground">{plan.name}</h4>
                        <p className="text-sm text-muted-foreground">{plan.description}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-foreground">{plan.price}</span>
                        <span className="text-muted-foreground">{plan.interval}</span>
                      </div>
                    </div>
                    {selectedPlan === plan.id && (
                      <CheckCircle2 className="absolute top-4 left-4 w-5 h-5 text-primary" />
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground text-center">
                  All plans include a <strong>7-day free trial</strong>. Cancel anytime.
                </p>
              </div>
              <div className="flex gap-2 mt-4">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowPlanDialog(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 btn-primary"
                  onClick={() => {
                    setShowPlanDialog(false);
                    handleSubscribe(selectedPlan);
                  }}
                  disabled={subscribing}
                >
                  {subscribing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Continue to Checkout'
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          <p className="text-xs text-muted-foreground">
            {subscriptionStatus === 'active'
              ? 'Your subscription renews automatically. Manage billing through Stripe.'
              : subscriptionStatus === 'comped'
              ? 'You have a complimentary plan from the Fencepost team. No payment needed during the comp period.'
              : 'Choose a plan to continue using Fencepost after your trial.'}
          </p>
        </CardContent>
      </Card>

      {/* Profile Card */}
      <Card className="card-farm" data-testid="profile-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            Farm Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              value={user?.email || ''}
              disabled
              className="input-farm bg-muted"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="farm_name">Farm Name</Label>
            <Input
              id="farm_name"
              value={formData.farm_name}
              onChange={(e) => setFormData({ ...formData, farm_name: e.target.value })}
              placeholder="e.g., Red Mesa Ranch"
              className="input-farm"
              data-testid="farm-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="farm_type">Farm Type</Label>
            <Select 
              value={formData.farm_type} 
              onValueChange={(v) => setFormData({ ...formData, farm_type: v })}
            >
              <SelectTrigger className="input-farm" data-testid="farm-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mixed">Mixed (Vegetables & Livestock)</SelectItem>
                <SelectItem value="vegetables">Vegetables / Crops</SelectItem>
                <SelectItem value="livestock">Livestock / Ranching</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Location</Label>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm"
                onClick={getLocation}
                className="text-primary"
              >
                <MapPin className="w-4 h-4 mr-1" />
                Detect
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                value={formData.location_lat}
                onChange={(e) => setFormData({ ...formData, location_lat: e.target.value })}
                placeholder="Latitude"
                className="input-farm"
                data-testid="lat-input"
              />
              <Input
                value={formData.location_lon}
                onChange={(e) => setFormData({ ...formData, location_lon: e.target.value })}
                placeholder="Longitude"
                className="input-farm"
                data-testid="lon-input"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Used for weather alerts and local recommendations
            </p>
          </div>

          <Button 
            onClick={handleSave}
            className="btn-primary w-full"
            disabled={saving}
            data-testid="save-profile-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Grant & Federal Identifiers Card — Feb 2026.
          Canonical surface for USDA/FSA/NRCS/SAM.gov identifiers used on
          grant applications and PDF exports. Reports > Farm Profile is now
          a read-only mirror that links back here. */}
      <Card className="card-farm" data-testid="grant-profile-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Grant & Federal Identifiers
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            One-time entry. These identifiers appear on the cover of every
            Fencepost grant / loan / audit report and cannot be edited from
            the Reports tab.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="legal_entity">Legal Entity Name</Label>
            <Input id="legal_entity" data-testid="profile-legal-entity-input"
              value={profileForm.legal_entity_name}
              onChange={(e) => setProfileForm({ ...profileForm, legal_entity_name: e.target.value })}
              placeholder="e.g., Star Farm LLC"
              className="input-farm" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="dba">DBA / LLC (if different)</Label>
            <Input id="dba" data-testid="profile-dba-input"
              value={profileForm.dba_llc_names}
              onChange={(e) => setProfileForm({ ...profileForm, dba_llc_names: e.target.value })}
              placeholder="Trade name(s)"
              className="input-farm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="fsa_farm">FSA Farm Serial #</Label>
              <Input id="fsa_farm" data-testid="profile-fsa-farm-input"
                value={profileForm.usda_farm_number}
                onChange={(e) => setProfileForm({ ...profileForm, usda_farm_number: e.target.value })}
                placeholder="From your local FSA office"
                className="input-farm" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="fsa_tract">FSA Tract #</Label>
              <Input id="fsa_tract" data-testid="profile-fsa-tract-input"
                value={profileForm.fsa_tract_number}
                onChange={(e) => setProfileForm({ ...profileForm, fsa_tract_number: e.target.value })}
                placeholder="Optional"
                className="input-farm" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground -mt-1">
            Your FSA Farm Serial Number is assigned by your local FSA office and is required for most USDA programs.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="uei">UEI (SAM.gov)</Label>
              <Input id="uei" data-testid="profile-uei-input"
                value={profileForm.uei}
                onChange={(e) => setProfileForm({ ...profileForm, uei: e.target.value.toUpperCase() })}
                placeholder="12 chars — federal grants req."
                maxLength={12}
                className="input-farm" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="duns">DUNS (legacy)</Label>
              <Input id="duns" data-testid="profile-duns-input"
                value={profileForm.duns}
                onChange={(e) => setProfileForm({ ...profileForm, duns: e.target.value })}
                placeholder="9 digits"
                maxLength={9}
                className="input-farm" />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ein">EIN / Tax ID</Label>
            <Input id="ein" data-testid="profile-ein-input"
              value={profileForm.ein}
              onChange={(e) => setProfileForm({ ...profileForm, ein: e.target.value })}
              placeholder={profileEinSet ? `Currently: ${profileEinMasked} — enter to change` : 'XX-XXXXXXX'}
              className="input-farm" />
            <p className="text-xs text-muted-foreground">Stored encrypted at rest; only the last 4 digits appear on exports.</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="prof_address">Mailing Address</Label>
            <Input id="prof_address" data-testid="profile-address-input"
              value={profileForm.address}
              onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
              placeholder="Street, City, State ZIP"
              className="input-farm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="prof_phone">Phone</Label>
              <Input id="prof_phone" data-testid="profile-phone-input"
                value={profileForm.phone}
                onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                placeholder="555-555-5555"
                className="input-farm" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="prof_email">Contact Email</Label>
              <Input id="prof_email" data-testid="profile-contact-email-input"
                value={profileForm.contact_email}
                onChange={(e) => setProfileForm({ ...profileForm, contact_email: e.target.value })}
                placeholder="Public-facing email"
                className="input-farm" />
            </div>
          </div>
          <Button onClick={handleSaveGrantProfile}
            className="btn-primary w-full"
            disabled={profileSaving || !profileLoaded}
            data-testid="save-grant-profile-btn">
            {profileSaving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>) : 'Save Grant Profile'}
          </Button>
        </CardContent>
      </Card>

      {/* Subscription Management */}
      {user?.subscription_status === 'active' && (
        <Card className="card-farm border-purple-500/30" data-testid="subscription-management-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Receipt className="w-5 h-5 text-purple-600" />
              Manage Subscription
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              View billing history, update payment method, or cancel your subscription through Stripe's secure customer portal.
            </p>
            <Button 
              variant="outline"
              className="w-full"
              onClick={async () => {
                try {
                  const res = await axios.post(
                    `${API_URL}/api/billing/customer-portal`,
                    { return_url: window.location.href },
                    { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
                  );
                  if (res.data.url) {
                    window.location.href = res.data.url;
                  }
                } catch (error) {
                  console.error('Portal error:', error);
                  toast.error('Failed to open billing portal');
                }
              }}
              data-testid="manage-billing-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Open Billing Portal
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Legal & App Info */}
      <Card className="card-farm" data-testid="app-info-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            About Fencepost
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-muted-foreground space-y-1">
            <p>Version 1.1.0</p>
            <p>Built for small farms, homesteads, and family ranches.</p>
            <p>This tool is for planning and awareness. Always consult professionals for important decisions.</p>
          </div>
          
          {/* Legal Links */}
          <div className="border-t pt-4 space-y-2">
            <p className="text-sm font-medium text-foreground">Legal</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Link to="/privacy" data-testid="privacy-policy-link">
                <Button variant="outline" size="sm" className="w-full sm:w-auto">
                  <Shield className="w-4 h-4 mr-2" />
                  Privacy Policy
                </Button>
              </Link>
              <Link to="/terms" data-testid="terms-of-service-link">
                <Button variant="outline" size="sm" className="w-full sm:w-auto">
                  <FileText className="w-4 h-4 mr-2" />
                  Terms of Service
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Support */}
          <div className="border-t pt-4 space-y-2">
            <p className="text-sm font-medium text-foreground">Support</p>
            <p className="text-sm text-muted-foreground">
              Questions? Email us at{' '}
              <a 
                href="mailto:support@myfencepost.com" 
                className="text-primary hover:underline"
                data-testid="support-email-link"
              >
                support@myfencepost.com
              </a>
            </p>
          </div>
          
          {/* Account Deletion */}
          <div className="border-t pt-4 space-y-2">
            <p className="text-sm font-medium text-destructive">Danger Zone</p>
            <p className="text-sm text-muted-foreground mb-3">
              Permanently delete your account and all associated farm data. This action cannot be undone.
            </p>
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="destructive" 
                  size="sm"
                  data-testid="delete-account-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete My Account
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-destructive flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Delete Account
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    This will permanently delete your account and all data including:
                  </p>
                  <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                    <li>All livestock and poultry records</li>
                    <li>Farm notes and chat history</li>
                    <li>Garden plans and layouts</li>
                    <li>Production and sustainability data</li>
                    <li>All uploaded photos</li>
                  </ul>
                  <div className="p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                    <p className="text-sm font-medium text-destructive">
                      This action cannot be undone.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-delete" className="text-sm">
                      Type <span className="font-mono font-bold">DELETE</span> to confirm
                    </Label>
                    <Input
                      id="confirm-delete"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Type DELETE"
                      data-testid="delete-confirm-input"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setDeleteDialogOpen(false);
                        setDeleteConfirmText('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      disabled={deleteConfirmText !== 'DELETE' || deleting}
                      onClick={handleDeleteAccount}
                      data-testid="confirm-delete-button"
                    >
                      {deleting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        'Delete Account'
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
